"use client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { FormField } from "@/schemas/type";
import useSWR from "swr";
import { getCountry } from "@/server/user";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import Input from "@/components/ui/input";
import { useState } from "react";

interface FormSelectProps {
  field?: FormField;
  placeholder?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
}

export default function FormAreaCode(props: FormSelectProps) {
  const locale = useCurrentLocale();

  const [search, setSearch] = useState("");

  const { data, isLoading } = useSWR(
    "areaCodeList",
    async() => {
      const res = await getCountry({
        filter: {
          level: {
            _eq: 1,
          },
          phone_code: {
            _nempty: true,
          },
        },
        fields: "name,phone_code,name_en",
      });

      const areaMap: Record<string, string[]> = {};

      res.data?.forEach((item) => {
        if (item.phone_code) {
          if (!areaMap[item.phone_code]) {
            areaMap[item.phone_code] = [locale === "cn" ? item.name : item.name_en];
          } else {
            areaMap[item.phone_code].push(locale === "cn" ? item.name : item.name_en);
          }
        }
      });

      return Object.keys(areaMap).map((key) => ({
        label: `+${key} ${areaMap[key].join(" / ")}`,
        value: key,
      }));
    }
  );

  return (
    <Select
      value={props.field?.value}
      onValueChange={(value) => {
        if (isLoading) return;

        props.field?.onChange?.(value);
      }}
      disabled={props?.disabled}>
      <SelectTrigger
        className={cn(
          "h-10 w-full rounded border-b border-[#F0F0F0] border-t-0 border-x-0 p-0 text-sm hover:border-[#cc2016] focus:border-[#cc2016] data-[disabled]:bg-[#F7F7F7] data-[placeholder]:text-[#A6A6A6] md:border md:border-[#C2C6CE] md:px-4",
          props.className
        )}>
        <SelectValue
          placeholder={
            <span
              className={cn(
                "after:pl-0.5 after:text-[#CC2016]",
                props.required ? "after:content-['*']" : ""
              )}>
              {props.placeholder}
            </span>
          }
        />
      </SelectTrigger>
      <SelectContent className="border-[#C2C6CE] bg-white relative pt-12">
        <div className="w-full fixed top-0 left-0 bg-white p-1 z-10">
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
            }}
          />
        </div>
        {data?.map((option, index) => (
          <SelectItem
            key={index}
            value={option.value}
            className={cn({
              "hidden": search && !option.label.toLocaleLowerCase().includes(search.toLocaleLowerCase())
            })}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
